import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import LegalAuthFooter from "../Legal/LegalAuthFooter";
import PasswordInput from "./PasswordInput";
import { readRememberLoginFormState } from "../../utils/rememberLogin";
import {
    GOOGLE_CLIENT_ID,
    mountGoogleSignInButton,
    setGoogleCredentialHandler,
} from "../../utils/googleAuth";

const FormLogin = ({ handleSubmit, handleChange, loading, error, onGoogleLogin, googleLoading }) => {
    const location = useLocation();
    const initialRememberState = readRememberLoginFormState();
    const googleOverlayRef = useRef(null);
    const [rememberMe, setRememberMe] = useState(initialRememberState.rememberMe);
    const [usernameOrEmail, setUsernameOrEmail] = useState(initialRememberState.usernameOrEmail);
    const [password, setPassword] = useState(initialRememberState.password);
    const [googleError, setGoogleError] = useState('');

    useEffect(() => {
        if (location.pathname !== '/login') return;
        const saved = readRememberLoginFormState();
        setRememberMe(saved.rememberMe);
        setUsernameOrEmail(saved.usernameOrEmail);
        setPassword(saved.password);
    }, [location.pathname, location.key]);

    const handleGoogleCredential = useCallback(async (response) => {
        if (!response?.credential || !onGoogleLogin) return;
        setGoogleError('');
        try {
            await onGoogleLogin(response.credential);
        } catch (err) {
            setGoogleError(err.message || 'Đăng nhập Google thất bại');
        }
    }, [onGoogleLogin]);

    useEffect(() => {
        setGoogleCredentialHandler(handleGoogleCredential);
    }, [handleGoogleCredential]);

    useEffect(() => {
        const container = googleOverlayRef.current;
        if (!GOOGLE_CLIENT_ID || !container) return undefined;
        return mountGoogleSignInButton(container);
    }, []);

    const displayError = error || googleError;

    return (
        <div className="tamcare-auth-card tamcare-auth-card--login">
            <div className="tamcare-auth-logo">
                <i className="fa-solid fa-hand-holding-heart"></i>
            </div>

            <div className="tamcare-auth-card__header tamcare-auth-card__header--center">
                <h2>Xin Chào!</h2>
                <p>Đăng nhập để chăm sóc sức khỏe tốt hơn</p>
            </div>

            <div className="tamcare-auth-error-slot" aria-live="polite">
                {displayError ? (
                    <div className="tamcare-auth-error">{displayError}</div>
                ) : null}
            </div>

            {GOOGLE_CLIENT_ID ? (
                <div className="tamcare-google-section">
                    <div
                        className={[
                            'tamcare-google-btn-shell',
                            googleLoading ? 'tamcare-google-btn-shell--loading' : '',
                        ].filter(Boolean).join(' ')}
                    >
                        <div className="tamcare-google-custom-face" aria-hidden="true">
                            <svg className="tamcare-google-icon" viewBox="0 0 24 24" aria-hidden="true">
                                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                            </svg>
                            <span>Đăng nhập bằng Google</span>
                        </div>
                        <div
                            ref={googleOverlayRef}
                            className="tamcare-google-btn-overlay"
                            aria-label="Đăng nhập bằng Google"
                        />
                    </div>
                </div>
            ) : (
                <p className="tamcare-google-hint tamcare-google-hint--warn">
                    Chưa cấu hình REACT_APP_GOOGLE_CLIENT_ID
                </p>
            )}

            <div className="tamcare-auth-divider">Hoặc</div>

            <form className="tamcare-auth-form" onSubmit={handleSubmit} method="POST">
                <div className="form-group">
                    <label htmlFor="usernameOrEmail">Tên đăng nhập hoặc Email</label>
                    <div className="tamcare-input-wrap">
                        <i className="fa-regular fa-user"></i>
                        <input
                            id="usernameOrEmail"
                            type="text"
                            name="usernameOrEmail"
                            placeholder="username hoặc email@example.com"
                            value={usernameOrEmail}
                            required
                            autoComplete="username"
                            onChange={(e) => {
                                setUsernameOrEmail(e.target.value);
                                handleChange(e);
                            }}
                        />
                    </div>
                </div>

                <div className="form-group">
                    <label htmlFor="password">Mật khẩu</label>
                    <PasswordInput
                        id="password"
                        name="password"
                        value={password}
                        required
                        autoComplete="current-password"
                        onChange={(e) => {
                            setPassword(e.target.value);
                            handleChange(e);
                        }}
                    />
                    <div className="tamcare-login-options">
                        <label className="tamcare-remember-me" htmlFor="rememberMe">
                            <input
                                id="rememberMe"
                                type="checkbox"
                                name="rememberMe"
                                value="1"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                            />
                            Ghi nhớ đăng nhập
                        </label>
                        <Link to="/forgot-password" className="tamcare-forgot-link">Quên mật khẩu?</Link>
                    </div>
                </div>

                <button type="submit" className="tamcare-auth-btn" disabled={loading || googleLoading}>
                    {loading ? "Đang xử lý..." : "Đăng Nhập"}
                    <i className="fa-solid fa-arrow-right-to-bracket"></i>
                </button>
            </form>

            <div className="tamcare-auth-footer tamcare-auth-footer--center">
                <span>Bạn chưa có tài khoản?</span>
                <Link to="/register">Đăng ký</Link>
            </div>

            <LegalAuthFooter />
        </div>
    );
};

export default FormLogin;
